import React, { PropsWithChildren, useState } from 'react';
import styled from 'styled-components';
import SearchBox from '../outline/SearchBox';

type ListSearchBoxProps = {
  className?: string;
  onSearch: () => void;
  onClear: () => void;
  /**
   * 접기/펴기를 켠다. 켜면 헤더가 붙고 접힌 상태가 브라우저에 기억된다.
   * 값은 화면을 구분하는 키다 (화면마다 취향이 다르다).
   */
  collapsibleKey?: string;
  /** 적용된 조건 수 — 접었을 때 "몇 개 걸려 있나"를 헤더에 보여준다 */
  activeCount?: number;
};

const STORAGE_PREFIX = 'admin:list-search-collapsed:';

/**
 * 접힘 상태를 읽는다. 사생활 보호 모드·사이트 데이터 차단에서는 접근 자체가 던지므로 감싼다.
 * 못 읽으면 **펼친 상태**로 시작한다 — 조건이 안 보이는 쪽보다 자리를 차지하는 쪽이 안전하다.
 */
const readCollapsed = (key: string): boolean => {
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsed = (key: string, collapsed: boolean): void => {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, String(collapsed));
  } catch {
    // 저장 못 해도 이번 세션 동작에는 지장이 없다 (다음에 열 때 펼쳐질 뿐)
  }
};

const StyledHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fafafa;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  .count {
    font-weight: 400;
    color: #6200ea;
  }

  .caret {
    margin-left: auto;
    color: #8c8c8c;
  }
`;

const StyledBody = styled.div<{ $hidden: boolean }>`
  /*
    접을 때 언마운트하지 않고 감춘다 — 언마운트하면 입력 중이던 값이 날아가고,
    펼칠 때 담당자 목록 같은 조회가 다시 나간다.
  */
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`;

/**
 * 리스트 검색 조건 영역 (AMZUWEB-684).
 *
 * 새로 만들지 않고 기존 `outline/SearchBox`(42곳 사용)를 감싼다 —
 * 검색/초기화 버튼 배치와 여백이 이미 어드민 전체의 관례라, 새 화면만 다르게 보이면
 * 공통화로 얻는 것보다 잃는 게 크다. 감싸는 이유는 **나중에 여기 한 곳만 고치면
 * 새 리스트 전부가 따라오게** 하기 위함이다.
 *
 * 필터가 8개쯤 되면 세로를 크게 먹어 표가 화면 밖으로 밀린다. `collapsibleKey` 를 주면
 * 접을 수 있고, 접힘 여부는 **브라우저에 기억**한다 — 필터 값(URL)과 달리 이건
 * *보는 사람의 취향*이라 링크로 공유될 성질이 아니다.
 */
export const ListSearchBox = ({
  className,
  onSearch,
  onClear,
  collapsibleKey,
  activeCount = 0,
  children,
}: PropsWithChildren<ListSearchBoxProps>): React.JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(() => (collapsibleKey ? readCollapsed(collapsibleKey) : false));

  const body = (
    <SearchBox className={className} onSearch={onSearch} onClear={onClear}>
      {children}
    </SearchBox>
  );

  if (!collapsibleKey) {
    return body;
  }

  const toggle = (): void => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    writeCollapsed(collapsibleKey, next);
  };

  return (
    <div>
      <StyledHeader type="button" onClick={toggle} aria-expanded={!isCollapsed}>
        <span>검색 조건</span>
        {/* 접었을 때 조건이 걸려 있는지 모르면 "왜 3건뿐이지" 의 답이 화면에서 사라진다 */}
        {activeCount > 0 && <span className="count">{activeCount}개 적용</span>}
        <span className="caret">{isCollapsed ? '▼ 펼치기' : '▲ 접기'}</span>
      </StyledHeader>

      <StyledBody $hidden={isCollapsed}>{body}</StyledBody>
    </div>
  );
};
